import { useTracker } from '../../state/store';
import type { Interpolation } from '../../engine/dsp/mixer';
import type { PaulaModel } from '../../engine/dsp/filters';
import { Field, Panel, Select, Slider, Toggle } from './primitives';

/**
 * Playback settings.
 *
 * These are the knobs that decide whether you are hearing an emulation of a
 * 1987 Amiga or a cleaned-up modern rendering of the same data — both are
 * legitimate, so both are one click apart.
 */
export function SettingsPanel() {
  const mixer = useTracker((s) => s.mixerSettings);
  const masterVolume = useTracker((s) => s.masterVolume);
  const loopSong = useTracker((s) => s.loopSong);
  const modPanning = useTracker((s) => s.modPanningCommand);
  const song = useTracker((s) => s.song);

  const setMixerSettings = useTracker((s) => s.setMixerSettings);
  const setMasterVolume = useTracker((s) => s.setMasterVolume);
  const setLoopSong = useTracker((s) => s.setLoopSong);
  const setModPanningCommand = useTracker((s) => s.setModPanningCommand);

  return (
    <Panel title="Sound">
      <div className="flex flex-col gap-1">
        <Field label="Interpolation">
          <Select<Interpolation>
            value={mixer.interpolation}
            onChange={(v) => setMixerSettings({ interpolation: v })}
            options={[
              { value: 'none', label: 'None (Amiga)' },
              { value: 'linear', label: 'Linear' },
              { value: 'cubic', label: 'Cubic' },
              { value: 'sinc', label: 'Sinc (best)' },
            ]}
          />
        </Field>

        <Field label="Output filter">
          <Select<PaulaModel>
            value={mixer.paulaModel}
            onChange={(v) => setMixerSettings({ paulaModel: v })}
            options={[
              { value: 'a500', label: 'A500' },
              { value: 'a1200', label: 'A1200' },
              { value: 'off', label: 'Off' },
            ]}
          />
        </Field>

        <Field label="Stereo width">
          <Slider
            value={1 - mixer.stereoSeparation}
            min={0}
            max={1}
            onChange={(v) => setMixerSettings({ stereoSeparation: 1 - v })}
          />
        </Field>

        <Field label="Volume ramp (ms)">
          <Slider
            value={mixer.rampMs}
            min={0}
            max={8}
            step={0.5}
            onChange={(v) => setMixerSettings({ rampMs: v })}
          />
        </Field>

        <Field label="Amplification">
          <Slider
            value={mixer.amplification}
            min={0.25}
            max={3}
            step={0.05}
            onChange={(v) => setMixerSettings({ amplification: v })}
          />
        </Field>

        <Field label="Master">
          <Slider value={masterVolume} min={0} max={1.5} onChange={setMasterVolume} />
        </Field>

        <div className="mt-1 border-t border-ink-700 pt-1">
          <Toggle checked={loopSong} label="Loop song" onChange={setLoopSong} />
          {song.format === 'mod' && (
            <Toggle
              checked={modPanning}
              label="MOD 8xx sets panning"
              onChange={setModPanningCommand}
            />
          )}
        </div>

        <p className="mt-1 font-mono text-[10px] leading-relaxed text-ink-500">
          {mixer.paulaModel === 'a500'
            ? 'A500: fixed 4.4 kHz filter plus the LED filter that effect E0x switches. This is what the samples were written for.'
            : mixer.paulaModel === 'a1200'
              ? 'A1200: no fixed filter, so the top end stays bright. Only the LED filter applies.'
              : 'Filtering off — the raw mix, brighter and harsher than any real Amiga.'}
        </p>

        {song.format === 'mod' && (
          <p className="font-mono text-[10px] leading-relaxed text-ink-500">
            MOD playback uses the PAL Paula clock, so middle C is 8287 Hz — about 16 cents flatter
            than FastTracker&nbsp;II&apos;s 8363 Hz. That difference is real hardware, not a bug.
          </p>
        )}
      </div>
    </Panel>
  );
}
